/**
 * The pure half of the Date Range node: how a stored bound decomposes into the
 * date / time / zone the inputs show, how it recomposes on edit, and how a pasted
 * schedule ("one game per line") becomes a list of windows.
 *
 * NO React and NO tldraw here, on purpose: `scripts/verify-time-window-schedule.mjs`
 * imports this file under a bare `node --experimental-strip-types`, so it has to
 * stay free of anything that does not survive type stripping (no enums, no
 * parameter properties) and of every import that needs a bundler.
 *
 * The wire contract these strings have to satisfy (both engines implement it):
 *   - a bare date `YYYY-MM-DD` is midnight in the NODE's offset;
 *   - a naive datetime `YYYY-MM-DDTHH:MM:SS` is wall time in the NODE's offset;
 *   - a datetime with an explicit zone (`Z` or `±HH:MM`) is ABSOLUTE and is not
 *     shifted by the node offset at all.
 * Every datetime this module emits carries seconds and, when zoned, the numeric
 * `±HH:MM` form rather than `Z`, so a stored string is self-describing.
 */

interface DecomposedBound {
  /** `YYYY-MM-DD`, or "" when the bound is empty or unreadable. */
  date: string;
  /** `HH:MM` (or `HH:MM:SS` when the seconds are not zero); "" for a bare date. */
  time: string;
  /** "" for a naive bound, otherwise the suffix verbatim: `Z` or `±HH:MM`. */
  zone: string;
}

const BOUND =
  /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:\d{2})?)?$/;

export function decomposeBound(bound: string): DecomposedBound {
  const m = BOUND.exec(bound.trim());
  if (!m) return { date: "", time: "", zone: "" };
  const [, date, hhmm, ss, zone] = m;
  const time = !hhmm ? "" : ss && ss !== "00" ? `${hhmm}:${ss}` : hhmm;
  return { date: date ?? "", time, zone: zone ?? "" };
}

/** Pads a `HH:MM` or `HH:MM:SS` (or `H:MM`) clock string to `HH:MM:SS`. */
function withSeconds(time: string): string {
  const [h = "0", m = "0", s = "0"] = time.split(":");
  return [h, m, s].map((part) => part.padStart(2, "0")).join(":");
}

/**
 * The inverse of `decomposeBound`. A date on its own stays a bare date; a date
 * with a time becomes a naive datetime; a zone that was already on the bound is
 * kept so an absolute bound stays absolute -- including when the author clears
 * its time, which then means midnight IN THAT ZONE rather than a bare date the
 * node offset would move.
 */
export function composeBound({ date, time, zone }: DecomposedBound): string {
  if (!date) return "";
  if (!time && !zone) return date;
  return `${date}T${withSeconds(time || "00:00")}${zone}`;
}

/** Minutes east of UTC as `+HH:MM` / `-HH:MM`; `+00:00` for UTC, never `Z`. */
export function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(
    abs % 60,
  ).padStart(2, "0")}`;
}

// --- IANA zones ------------------------------------------------------------------------

/**
 * The zones the "Times are in" picker offers. `Intl.supportedValuesOf` is the only
 * way to enumerate them; where it is missing the browser's own zone is the one
 * zone we can still name.
 */
export function availableZones(): string[] {
  const intl = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intl.supportedValuesOf === "function") {
    try {
      const zones = intl.supportedValuesOf("timeZone");
      if (zones.length > 0) return zones;
    } catch {
      // fall through to the browser zone
    }
  }
  return [browserZone()];
}

export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

interface Wall {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
}

function wallToUtcMs(w: Wall): number {
  return Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s);
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(zone, f);
  }
  return f;
}

/** The wall clock in `zone` at the instant `ms`. */
function wallInZone(ms: number, zone: string): Wall {
  const parts: Record<string, number> = {};
  for (const p of formatterFor(zone).formatToParts(new Date(ms))) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  return {
    y: parts.year ?? 1970,
    mo: parts.month ?? 1,
    d: parts.day ?? 1,
    // Some engines print midnight as "24" under h23; read it as 0.
    h: (parts.hour ?? 0) % 24,
    mi: parts.minute ?? 0,
    s: parts.second ?? 0,
  };
}

/** Minutes east of UTC that `zone` is at the instant `ms`. */
function offsetAtInstant(ms: number, zone: string): number {
  return Math.round((wallToUtcMs(wallInZone(ms, zone)) - ms) / 60_000);
}

/**
 * The UTC offset, in minutes, that `zone` has at the given WALL time. Standard
 * two-pass trick: read the wall time as if it were UTC, ask the zone what offset
 * it has then, shift by that, and ask once more so an instant near a DST edge
 * settles on the offset actually in force.
 */
export function zoneOffsetMinutes(zone: string, wall: Wall): number {
  const guess = wallToUtcMs(wall);
  const first = offsetAtInstant(guess, zone);
  const second = offsetAtInstant(guess - first * 60_000, zone);
  if (second === first) return first;
  return offsetAtInstant(guess - second * 60_000, zone);
}

// --- Schedule parsing --------------------------------------------------------------------

export type ScheduleZone = { kind: "node" } | { kind: "iana"; zone: string };

interface TimeWindowBounds {
  start: string;
  end: string;
}

interface ParsedSchedule {
  windows: TimeWindowBounds[];
  /** 1-based numbers of the lines that could not be read. Empty when all parsed. */
  errors: number[];
}

interface Side {
  date?: { y: number; mo: number; d: number };
  time?: { h: number; mi: number; s: number };
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME = /^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i;

function validDate(y: number, mo: number, d: number): boolean {
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  return (
    back.getUTCFullYear() === y &&
    back.getUTCMonth() === mo - 1 &&
    back.getUTCDate() === d
  );
}

function parseTime(text: string): Side["time"] | undefined {
  const m = TIME.exec(text.trim());
  if (!m) return undefined;
  let h = Number(m[1]);
  const mi = Number(m[2] ?? "0");
  const s = Number(m[3] ?? "0");
  const meridiem = m[4]?.toLowerCase().replace(/\./g, "");
  if (meridiem) {
    if (h < 1 || h > 12) return undefined;
    if (meridiem === "am") h = h === 12 ? 0 : h;
    else h = h === 12 ? 12 : h + 12;
  } else if (h > 23) {
    return undefined;
  }
  // A bare "7" with no am/pm and no minutes is more likely a typo than 07:00.
  if (!meridiem && m[2] === undefined) return undefined;
  if (mi > 59 || s > 59) return undefined;
  return { h, mi, s };
}

/** One side of a window: an optional date, then an optional time. */
function parseSide(text: string): Side | undefined {
  let rest = text.trim();
  const side: Side = {};
  const dm = DATE.exec(rest);
  if (dm) {
    const y = Number(dm[1]);
    const mo = Number(dm[2]);
    const d = Number(dm[3]);
    if (!validDate(y, mo, d)) return undefined;
    side.date = { y, mo, d };
    rest = rest.slice(dm[0].length).replace(/^(?:T|\s+)/i, "");
  }
  if (rest) {
    const time = parseTime(rest);
    if (!time) return undefined;
    side.time = time;
  }
  if (!side.date && !side.time) return undefined;
  return side;
}

// Separators between the two sides. A hyphen only counts when it has whitespace
// on both sides, so the hyphens inside `2026-10-22` never split a date. The
// dashes (– —) and the arrow do not appear in dates and may sit tight.
const SEPARATOR = /\t|\s+-\s+|\s*[–—→]\s*|\s*,\s*|\s+to\s+/i;

function nextDay(date: { y: number; mo: number; d: number }) {
  const back = new Date(Date.UTC(date.y, date.mo - 1, date.d + 1));
  return {
    y: back.getUTCFullYear(),
    mo: back.getUTCMonth() + 1,
    d: back.getUTCDate(),
  };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatDate(date: { y: number; mo: number; d: number }): string {
  return `${date.y}-${pad(date.mo)}-${pad(date.d)}`;
}

function emit(wall: Wall, dated: boolean, zone: ScheduleZone): string {
  const date = formatDate(wall);
  if (zone.kind === "node") {
    // A midnight with no time typed is a bare date: it means the same thing to
    // the engines and reads the way the author wrote it.
    if (!dated) return date;
    return `${date}T${pad(wall.h)}:${pad(wall.mi)}:${pad(wall.s)}`;
  }
  // A named zone makes every bound absolute, midnights included -- a bare date
  // would be a day in the NODE's offset, which is the wrong day for half the
  // world.
  const offset = zoneOffsetMinutes(zone.zone, wall);
  return `${date}T${pad(wall.h)}:${pad(wall.mi)}:${pad(wall.s)}${formatOffset(
    offset,
  )}`;
}

function parseLine(line: string, zone: ScheduleZone): TimeWindowBounds | undefined {
  const parts = line.split(SEPARATOR).filter((p) => p.trim() !== "");
  if (parts.length === 1) {
    // A date on its own is the whole day.
    const side = parseSide(parts[0]!);
    if (!side?.date || side.time) return undefined;
    return {
      start: emit({ ...side.date, h: 0, mi: 0, s: 0 }, false, zone),
      end: emit({ ...nextDay(side.date), h: 0, mi: 0, s: 0 }, false, zone),
    };
  }
  if (parts.length !== 2) return undefined;
  const start = parseSide(parts[0]!);
  const end = parseSide(parts[1]!);
  if (!start?.date || !end) return undefined;

  const startWall: Wall = {
    ...start.date,
    ...(start.time ?? { h: 0, mi: 0, s: 0 }),
  };
  let endDate = end.date ?? start.date;
  const endTime = end.time ?? { h: 0, mi: 0, s: 0 };
  let endWall: Wall = { ...endDate, ...endTime };
  if (!end.date && wallToUtcMs(endWall) <= wallToUtcMs(startWall)) {
    // "19:00 - 01:30" is a game that ran past midnight, not one that ended
    // before it began.
    endDate = nextDay(start.date);
    endWall = { ...endDate, ...endTime };
  }
  const dated = Boolean(start.time || end.time);
  return {
    start: emit(startWall, dated, zone),
    end: emit(endWall, dated, zone),
  };
}

/**
 * One window per line, blank lines ignored. When any line cannot be read the
 * result carries its 1-based number and NO windows, so a half-parsed schedule is
 * never appended to a node.
 */
export function parseSchedule(text: string, zone: ScheduleZone): ParsedSchedule {
  const windows: TimeWindowBounds[] = [];
  const errors: number[] = [];
  text.split(/\r?\n/).forEach((raw, index) => {
    const line = raw.trim();
    if (!line) return;
    const window = parseLine(line, zone);
    if (window) windows.push(window);
    else errors.push(index + 1);
  });
  return { windows: errors.length ? [] : windows, errors };
}

// --- Usable windows ----------------------------------------------------------------------
//
// Both engines fail CLOSED on a window they cannot use: a blank bound, an unreadable
// one, an hour outside 0-23, or a range that ends at or before its start makes the
// WHOLE node match nothing. That is deliberate there ("a typo in game 40 should be
// visibly broken, not silently 99 games"), but the engines have no way to SAY which
// window is broken. This is where the canvas says it. One rule, read by the node's
// caption, by the save gate (via a custom-validity message) and by the verify script,
// so the three cannot disagree about what "usable" means.
//
// 2026-09-15: an author pasted an 80-game schedule into a node that had started with
// one empty range. Paste appended below it, the empty row sat unnoticed at the top,
// and every post failed Debug with no message anywhere. These messages are the
// message that was missing.

/** One thing wrong with one window, positioned the way the canvas labels its rows. */
export interface WindowProblem {
  /** 0-based index into the node's windows; -1 when the problem is the list itself. */
  index: number;
  message: string;
}

/** A whole hour 0-23, or undefined. Mirrors the engines' `hour_arg` / `_whole_hour`. */
function hourOf(text: string): number | undefined {
  const t = text.trim();
  if (!/^\d{1,2}$/.test(t)) return undefined;
  const h = Number(t);
  return h <= 23 ? h : undefined;
}

export function hourWindowProblems(windows: TimeWindowBounds[]): WindowProblem[] {
  if (windows.length === 0) {
    return [{ index: -1, message: "This node has no windows." }];
  }
  const problems: WindowProblem[] = [];
  windows.forEach((w, index) => {
    const label = `Window ${index + 1}`;
    const start = hourOf(w.start);
    const end = hourOf(w.end);
    if (start === undefined && end === undefined) {
      problems.push({ index, message: `${label} has no hours.` });
    } else if (start === undefined) {
      problems.push({ index, message: `${label} has no start hour.` });
    } else if (end === undefined) {
      problems.push({ index, message: `${label} has no end hour.` });
    } else if (start === end) {
      // The engines answer false for a window whose start is its end: the end is not
      // included, so there is nothing left in it.
      problems.push({
        index,
        message: `${label} starts and ends at the same hour, so it holds no time.`,
      });
    }
  });
  return problems;
}

// The wire grammar both engines read, which is WIDER than `BOUND` above: seconds may
// carry a fraction, the zone may be written `±HHMM`, and lower-case `z` is accepted.
// The inputs cannot show every one of those forms, but a manifest that carries one is
// not broken and must not be flagged as such.
const WIRE_BOUND =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|z|[+-]\d{2}:?\d{2})?)?$/;

/** `Z`, `+05:30` or `-0400` as minutes east of UTC. */
function zoneMinutes(zone: string): number {
  if (zone === "Z" || zone === "z") return 0;
  const sign = zone.startsWith("-") ? -1 : 1;
  const digits = zone.slice(1).replace(":", "");
  return sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4)));
}

/**
 * The instant a stored bound denotes, in epoch milliseconds, read the way both engines
 * read it: a bare date is midnight and a naive time is wall time in the NODE's offset;
 * a bound that names its own zone is absolute; a bare number is an epoch in seconds,
 * treated as naive. Undefined when the engines could not read it either.
 */
export function boundInstantMs(
  bound: string,
  nodeOffsetMinutes: number,
): number | undefined {
  const text = bound.trim();
  if (/^\d{9,}(?:\.\d+)?$/.test(text)) {
    return Number(text) * 1000 - nodeOffsetMinutes * 60_000;
  }
  const m = WIRE_BOUND.exec(text);
  if (!m) return undefined;
  const [, y, mo, d, hh, mi, ss, zone] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  if (!validDate(year, month, day)) return undefined;
  const h = Number(hh ?? "0");
  const min = Number(mi ?? "0");
  const s = Number(ss ?? "0");
  if (h > 23 || min > 59 || s > 59) return undefined;
  const wall = Date.UTC(year, month - 1, day, h, min, s);
  const offset = zone ? zoneMinutes(zone) : nodeOffsetMinutes;
  return wall - offset * 60_000;
}

export function dateWindowProblems(
  windows: TimeWindowBounds[],
  nodeOffsetMinutes: number,
): WindowProblem[] {
  if (windows.length === 0) {
    return [{ index: -1, message: "This node has no ranges." }];
  }
  const problems: WindowProblem[] = [];
  windows.forEach((w, index) => {
    const label = `Range ${index + 1}`;
    const start = w.start.trim();
    const end = w.end.trim();
    if (!start && !end) {
      problems.push({ index, message: `${label} is empty. Fill it in or remove it.` });
      return;
    }
    if (!start) {
      problems.push({ index, message: `${label} has no start date.` });
      return;
    }
    if (!end) {
      problems.push({ index, message: `${label} has no end date.` });
      return;
    }
    const lower = boundInstantMs(start, nodeOffsetMinutes);
    const upper = boundInstantMs(end, nodeOffsetMinutes);
    if (lower === undefined) {
      problems.push({ index, message: `${label}: “${start}” is not a date.` });
      return;
    }
    if (upper === undefined) {
      problems.push({ index, message: `${label}: “${end}” is not a date.` });
      return;
    }
    if (lower === upper) {
      // The same bare date on both sides is the likeliest way here: "the 15th to the
      // 15th" reads as one day and is in fact none.
      problems.push({
        index,
        message: `${label} ends the moment it starts, so it holds no time. The end is not included: to cover all of the 15th, end on the 16th.`,
      });
    } else if (lower > upper) {
      problems.push({ index, message: `${label} ends before it starts.` });
    }
  });
  return problems;
}
